// Promise-based confirmation dialogs: `if (await confirm({...})) …`.
import { useEffect, useState } from 'react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { buttonVariants } from '@/components/ui/button';

interface ConfirmOptions {
  title: string;
  body: string;
  confirm?: string;
  danger?: boolean;
  /** Ask the person to type this text before the button enables. */
  typeToConfirm?: string;
}
type Pending = ConfirmOptions & { resolve: (ok: boolean) => void };

let show: ((p: Pending) => void) | null = null;

export function confirm(opts: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => (show ? show({ ...opts, resolve }) : resolve(false)));
}

export function ConfirmHost() {
  const [p, setP] = useState<Pending | null>(null);
  const [typed, setTyped] = useState('');
  useEffect(() => {
    show = (x) => {
      setTyped('');
      setP(x);
    };
    return () => {
      show = null;
    };
  }, []);
  const close = (ok: boolean) => {
    p?.resolve(ok);
    setP(null);
  };
  const blocked = !!p?.typeToConfirm && typed.trim() !== p.typeToConfirm;
  return (
    <AlertDialog open={!!p} onOpenChange={(o) => !o && close(false)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{p?.title}</AlertDialogTitle>
          <AlertDialogDescription>{p?.body}</AlertDialogDescription>
        </AlertDialogHeader>
        {p?.typeToConfirm ? (
          <div className="grid gap-2">
            <Label htmlFor="type-confirm">Type “{p.typeToConfirm}” to confirm</Label>
            <Input id="type-confirm" autoComplete="off" autoFocus value={typed} onChange={(e) => setTyped(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && !blocked && close(true)} />
          </div>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction disabled={blocked} className={p?.danger ? buttonVariants({ variant: 'destructive' }) : undefined} onClick={(e) => { e.preventDefault(); if (!blocked) close(true); }}>
            {p?.confirm ?? 'Confirm'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
